import React from "react";
import { Reveal, Eyebrow, PageHeader } from "../components/common";
import { CONTACT_INFO } from "../mock";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    intro:
      "We only collect information that you choose to give us directly — we do not passively track visitors. This includes:",
    points: [
      {
        label: "Personal Information",
        body:
          "Your name, contact details (email, phone number), company or dealership information, and any other information you voluntarily provide through our enquiry form, career/job application form (including your resume), or direct communication with us.",
      },
    ],
  },
  {
    title: "2. Cookies",
    intro:
      "This website does not currently use tracking, analytics, or marketing cookies. If we introduce any such cookies in the future, you will be shown a cookie banner and asked to choose one of the following:",
    points: [
      { label: "Allow All", body: "Necessary cookies plus any analytics or marketing cookies we may introduce." },
      { label: "Allow Necessary Only", body: "Only the cookies required for the site to function." },
      { label: "Decline", body: "No cookies beyond what is strictly necessary for the site to function." },
    ],
  },
  {
    title: "3. How We Use Your Information",
    intro: "The data we collect is used for the following purposes:",
    points: [
      { label: "Service Improvement", body: "To enhance our offerings and understand customer needs across our Maruti Suzuki, Hero MotoCorp, and Amaron Battery product lines." },
      { label: "Communication", body: "To respond to enquiries, process job applications, send important updates, and share information about our products and services." },
      { label: "Compliance", body: "To comply with legal obligations and protect the rights and safety of Rajiv Syndicate Group, our customers, and our website." },
    ],
  },
  {
    title: "4. Sharing Your Information",
    intro: "Rajiv Syndicate Group does not sell, trade, or otherwise transfer your personal information to third parties, except in the following cases:",
    points: [
      { label: "Service Providers", body: "We may share your information with trusted service providers who assist us in operating our website and providing our services, as long as they agree to keep your information confidential." },
      { label: "Legal Requirements", body: "We may disclose your information to comply with legal processes or to protect our rights, property, and safety." },
    ],
  },
];

const SIMPLE_SECTIONS = [
  {
    title: "5. Security of Your Information",
    body:
      "We implement industry-standard measures to protect your personal data from unauthorised access, alteration, disclosure, or destruction. However, no internet transmission is completely secure, so while we strive to protect your information, we cannot guarantee absolute security.",
  },
  {
    title: "7. Changes to This Privacy Policy",
    body:
      "Rajiv Syndicate Group may update this Privacy Policy periodically to reflect changes in our practices. We encourage you to review this page regularly for the latest information on our privacy practices.",
  },
];

function PolicySection({ title, intro, points, delay, id }) {
  return (
    <Reveal delay={delay}>
      <div id={id} className="py-9 border-b border-[var(--rs-border)] scroll-mt-24">
        <h2 className="font-head font-bold text-2xl">{title}</h2>
        {intro && <p className="text-[var(--rs-muted)] mt-3 leading-relaxed">{intro}</p>}
        {points && (
          <ul className="mt-5 space-y-4">
            {points.map((p) => (
              <li key={p.label} className="flex gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--rs-blue)] shrink-0 mt-2.5" />
                <p className="text-[var(--rs-muted)] leading-relaxed">
                  <span className="text-[var(--rs-ink)] font-semibold">{p.label}: </span>
                  {p.body}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Reveal>
  );
}

export default function PrivacyPolicy() {
  return (
    <>
      <PageHeader
        crumb="Privacy Policy"
        title="Privacy Policy"
        intro="At Rajiv Syndicate Group, we value your privacy and are committed to protecting the personal information you share with us. This Privacy Policy outlines the types of information we collect, how we use it, and your rights in relation to your personal data. By using our website, you agree to the terms outlined in this Privacy Policy."
      />

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-20">
        <Reveal><Eyebrow>Last updated · 2026</Eyebrow></Reveal>

        {SECTIONS.map((s, i) => (
          <PolicySection key={s.title} {...s} delay={i * 40} />
        ))}

        <Reveal delay={0}>
          <div className="py-9 border-b border-[var(--rs-border)]">
            <h2 className="font-head font-bold text-2xl">{SIMPLE_SECTIONS[0].title}</h2>
            <p className="text-[var(--rs-muted)] mt-3 leading-relaxed">{SIMPLE_SECTIONS[0].body}</p>
          </div>
        </Reveal>

        <Reveal delay={40}>
          <div className="py-9 border-b border-[var(--rs-border)]">
            <h2 className="font-head font-bold text-2xl">6. Your Rights</h2>
            <p className="text-[var(--rs-muted)] mt-3 leading-relaxed">
              You have the right to access, correct, or delete your personal information held by Rajiv Syndicate Group. If you would
              like to exercise these rights or have any questions regarding your personal data, please contact us at{" "}
              <a href={`mailto:${CONTACT_INFO.general}`} className="text-[var(--rs-blue)] font-medium hover:underline">
                {CONTACT_INFO.general}
              </a>.
            </p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="py-9 border-b border-[var(--rs-border)]">
            <h2 className="font-head font-bold text-2xl">{SIMPLE_SECTIONS[1].title}</h2>
            <p className="text-[var(--rs-muted)] mt-3 leading-relaxed">{SIMPLE_SECTIONS[1].body}</p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="py-9">
            <h2 className="font-head font-bold text-2xl">8. Contact Us</h2>
            <p className="text-[var(--rs-muted)] mt-3 leading-relaxed">
              If you have any questions or concerns about our Privacy Policy or how we handle your personal data, please contact us at:
            </p>
            <div className="mt-6 bg-[var(--rs-bg)] border border-[var(--rs-border)] rounded-2xl p-7">
              <p className="font-head font-bold text-lg">Rajiv Syndicate Group</p>
              <p className="text-[var(--rs-muted)] mt-1">{CONTACT_INFO.address.join(" ")}</p>
              <p className="text-[var(--rs-muted)] mt-3">
                Email:{" "}
                <a href={`mailto:${CONTACT_INFO.general}`} className="text-[var(--rs-blue)] font-medium hover:underline">
                  {CONTACT_INFO.general}
                </a>
              </p>
              <p className="text-[var(--rs-muted)] mt-1">
                Phone:{" "}
                <a href="tel:+919215926601" className="text-[var(--rs-blue)] font-medium hover:underline">
                  {CONTACT_INFO.accounts[0]}
                </a>
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
